import { apiClient } from './api-client';
import { Listing, ListingForm, APIResponse } from '@/types';

export class ListingsService {
  // Get all listings for the current user
  static async getListings(): Promise<Listing[]> {
    const response = await apiClient.get<Listing[]>('/listings');
    return response.data || [];
  }

  // Get a specific listing by ID
  static async getListing(listingId: string): Promise<Listing> {
    const response = await apiClient.get<Listing | Listing[]>(`/listings/${listingId}`);
    
    console.log('ListingsService.getListing - Raw response:', response);
    console.log('ListingsService.getListing - Response data:', response.data);
    
    // Handle case where API returns an array instead of a single object
    if (Array.isArray(response.data)) {
      console.log('ListingsService.getListing - Data is array, finding listing with ID:', listingId);
      const listing = response.data.find(l => l.listingId === listingId);
      if (!listing) {
        throw new Error(`Listing with ID ${listingId} not found`);
      }
      console.log('ListingsService.getListing - Found listing:', listing);
      return listing;
    }
    
    console.log('ListingsService.getListing - Data is single object:', response.data);
    return response.data as Listing;
  }

  // Create a new listing
  static async createListing(listingData: ListingForm): Promise<Listing> {
    const response = await apiClient.post<Listing>('/listings', listingData);
    return response.data;
  }

  // Update an existing listing
  static async updateListing(listingId: string, listingData: Partial<ListingForm>): Promise<Listing> {
    const response = await apiClient.put<Listing>(`/listings/${listingId}`, listingData);
    return response.data;
  }

  // Delete a listing
  static async deleteListing(listingId: string): Promise<void> {
    await apiClient.delete(`/listings/${listingId}`);
  }

  // Upload photos for a listing
  static async uploadListingPhoto(listingId: string, file: File): Promise<string> {
    // Get presigned URL for upload
    const response = await apiClient.post<{ uploadUrl: string; photoUrl: string; uploadId: string }>(`/listings/${listingId}/photos/upload`, {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    });

    const { uploadUrl, photoUrl, uploadId } = response.data;

    // Upload file to S3 using presigned URL
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload photo');
    }

    // Confirm the upload
    await apiClient.post(`/listings/${listingId}/photos/confirm`, {
      uploadId,
      photoUrl,
    });

    return photoUrl;
  }

  // Delete a photo from a listing
  static async deleteListingPhoto(listingId: string, photoUrl: string): Promise<void> {
    const encodedPhotoUrl = encodeURIComponent(photoUrl);
    await apiClient.delete(`/listings/${listingId}/photos?photoUrl=${encodedPhotoUrl}`);
  }

  // Update listing status
  static async updateListingStatus(listingId: string, status: 'draft' | 'published' | 'paused'): Promise<Listing> {
    const response = await apiClient.put<Listing>(`/listings/${listingId}`, { status });
    return response.data;
  }

  // Activate an imported listing (transition from inactive to draft)
  static async activateListing(listingId: string): Promise<Listing> {
    const response = await apiClient.put<Listing>(`/listings/${listingId}/activate`, {});
    return response.data;
  }

  // Validate listing before activation
  static validateListingForActivation(listing: Listing): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Check required fields
    if (!listing.title?.trim()) {
      errors.push('Title is required');
    }

    if (!listing.description?.trim()) {
      errors.push('Description is required');
    }

    if (!listing.address?.street?.trim()) {
      errors.push('Street address is required');
    }

    if (!listing.address?.city?.trim()) {
      errors.push('City is required');
    }

    if (!listing.address?.state?.trim()) {
      errors.push('State is required');
    }

    if (!listing.address?.zipCode?.trim()) {
      errors.push('ZIP code is required');
    }

    if (!listing.pricing?.basePrice || listing.pricing.basePrice <= 0) {
      errors.push('Base price must be greater than 0');
    }

    if (!listing.maxGuests || listing.maxGuests < 1) {
      errors.push('Must accommodate at least 1 guest');
    }

    if (!listing.bedrooms || listing.bedrooms < 1) {
      errors.push('Must have at least 1 bedroom');
    }

    if (!listing.bathrooms || listing.bathrooms < 1) {
      errors.push('Must have at least 1 bathroom');
    }

    if (!listing.photos || listing.photos.length === 0) {
      errors.push('At least one photo is required');
    }

    if (!listing.amenities || listing.amenities.length === 0) {
      errors.push('At least one amenity must be selected');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Check if listing is complete and ready to publish
  static checkListingCompleteness(listing: Partial<ListingForm>): {
    isComplete: boolean;
    completedSections: string[];
    missingSections: string[];
    completionPercentage: number;
  } {
    const sections = [
      { 
        name: 'Basic Info', 
        check: () => listing.title && listing.description && listing.propertyType && listing.roomType
      },
      { 
        name: 'Location', 
        check: () => listing.address?.street && listing.address?.city && listing.address?.state && listing.address?.zipCode && listing.address?.country 
      },
      { 
        name: 'Capacity', 
        check: () => listing.maxGuests && listing.bedrooms !== undefined && listing.bathrooms && listing.maxGuests > 0 && listing.bedrooms >= 0 && listing.bathrooms >= 0.5
      },
      { 
        name: 'Amenities', 
        check: () => true // Amenities are optional, so always considered complete
      },
      { 
        name: 'Pricing', 
        check: () => listing.pricing?.basePrice && listing.pricing?.currency && listing.pricing.basePrice > 0
      },
      { 
        name: 'Rules & Policies', 
        check: () => listing.checkInTime && listing.checkOutTime && listing.minimumStay && listing.cancellationPolicy && listing.minimumStay >= 1
      },
      { 
        name: 'Photos', 
        check: () => true // Photos are recommended but not required for completion
      },
    ];

    const completedSections = sections.filter(section => section.check()).map(section => section.name);
    const missingSections = sections.filter(section => !section.check()).map(section => section.name);
    const completionPercentage = Math.round((completedSections.length / sections.length) * 100);

    return {
      isComplete: missingSections.length === 0,
      completedSections,
      missingSections,
      completionPercentage,
    };
  }
}

export default ListingsService;